import { useState } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../../utils/cn";

type SlidePanelProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  contentClassName?: string;
  children: ReactNode;
  labelledBy?: string;
};

export const SlidePanel = ({
  open,
  defaultOpen,
  onOpenChange,
  className,
  contentClassName,
  children,
  labelledBy,
  ...props
}: SlidePanelProps) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? false);
  const isControlled = typeof open === "boolean";
  const isOpen = isControlled ? (open as boolean) : internalOpen;

  const setOpen = (next: boolean) => {
    if (!isControlled) {
      setInternalOpen(next);
    }
    onOpenChange?.(next);
  };

  return (
    <div
      {...props}
      className={cn(
        "grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none",
        isOpen
          ? "grid-rows-[1fr] opacity-100 pointer-events-auto"
          : "grid-rows-[0fr] opacity-0 pointer-events-none",
        className
      )}
    >
      <div
        className={cn("min-h-0 overflow-hidden", contentClassName)}
        role="region"
        aria-labelledby={labelledBy}
        aria-hidden={!isOpen}
      >
        {children}
      </div>
    </div>
  );
};

export type { SlidePanelProps };
